
import { Vue, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import API from '@/lib/API';
import { currentUserHasPermission } from '@/helpers';
import {
  Rule, RuleConditionDefinition, RuleEvent, RuleExecution, RuleExecutionOutcome, RuleRunsSummary,
} from '@/types';
import {
  OUTCOME_COLORS, OUTCOME_LABELS, nodeLabel, stoppingNode,
} from '@/helpers/ruleLogHelpers';
import { leadRouteCode } from '@/helpers/leadCode';
import RuleExecutionDetails from '@/components/rules/RuleExecutionDetails.vue';

interface ExecutionFilters {
  ruleId: number | null;
  lead: string | null;
  event: string | null;
  outcomes: RuleExecutionOutcome[];
  from: string;
  to: string;
}

const PAGE_SIZE = 50;

// Giorno corrente a Roma (YYYY-MM-DD), indipendente dal fuso del browser.
// Il frontend non ha moment-timezone fra le dipendenze: Intl basta per il solo giorno.
function todayInRome(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => (parts.find((p) => p.type === type) || { value: '' }).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

@Component({ components: { RuleExecutionDetails } })
export default class RuleExecutions extends Vue {
  loading = false

  loadError: string | null = null

  items: RuleExecution[] = []

  total = 0

  expanded: RuleExecution[] = []

  details: Record<number, RuleExecution> = {}

  detailErrors: Record<number, boolean> = {}

  summary: RuleRunsSummary = { anomalies: [], nights: [] }

  tableOptions: { page?: number } = { page: 1 }

  leadInput: string | null = null

  requestSeq = 0

  headers = [
    {
      text: 'Data', value: 'createdAt', sortable: false, width: '160px',
    },
    { text: 'Evento', value: 'eventCodename', sortable: false },
    { text: 'Lead', value: 'lead', sortable: false },
    { text: 'Regola', value: 'ruleName', sortable: false },
    { text: 'Causato da', value: 'user', sortable: false },
    { text: 'Esito', value: 'outcome', sortable: false },
    { text: '', value: 'data-table-expand' },
  ]

  outcomeItems = (Object.keys(OUTCOME_LABELS) as RuleExecutionOutcome[])
    .map((value) => ({ value, text: OUTCOME_LABELS[value] }))

  get filters(): ExecutionFilters {
    const q = this.$route.query;
    const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
    const esito = str(q.esito);
    return {
      ruleId: str(q.regola) ? Number(q.regola) : null,
      lead: str(q.lead),
      event: str(q.evento),
      outcomes: esito ? (esito.split(',') as RuleExecutionOutcome[]) : [],
      from: str(q.dal) || moment(todayInRome(), 'YYYY-MM-DD').subtract(6, 'days').format('YYYY-MM-DD'),
      to: str(q.al) || todayInRome(),
    };
  }

  get rules(): Rule[] { return this.$store.getters.rules || []; }

  get ruleItems(): Array<{ value: number; text: string }> {
    return this.rules.map((r) => ({ value: r.id, text: r.name }));
  }

  get events(): RuleEvent[] { return this.$store.getters.ruleEvents || []; }

  get eventItems(): Array<{ value: string; text: string }> {
    return this.events.map((e) => ({ value: e.codename, text: e.name }));
  }

  get conditionDefinitions(): RuleConditionDefinition[] {
    return this.$store.getters.ruleConditionDefinitions || [];
  }

  get canManage(): boolean { return currentUserHasPermission('rules.manage'); }

  get showNights(): boolean {
    return this.summary.nights.length > 0 && !this.filters.lead && !this.filters.ruleId
      && (!this.filters.event || this.filters.event === 'time.everyday');
  }

  // Le regole valutate nello stesso trigger hanno lo stesso sfondo, che si
  // alterna da un trigger all'altro
  get shadedRows(): Record<number, boolean> {
    const shaded: Record<number, boolean> = {};
    let current = false;
    let lastRun: number | null = null;
    this.items.forEach((item) => {
      if (item.RuleRunId !== lastRun) {
        current = !current;
        lastRun = item.RuleRunId;
      }
      shaded[item.id] = current;
    });
    return shaded;
  }

  rowClass(item: RuleExecution): string {
    return this.shadedRows[item.id] ? 'run-shaded' : '';
  }

  ruleExists(id: number): boolean {
    return this.rules.some((r) => r.id === id);
  }

  eventName(codename: string): string {
    const event = this.events.find((e) => e.codename === codename);
    return event ? event.name : codename;
  }

  outcomeLabel(outcome: RuleExecutionOutcome): string { return OUTCOME_LABELS[outcome] || outcome; }

  outcomeColor(outcome: RuleExecutionOutcome): string { return OUTCOME_COLORS[outcome] || 'grey'; }

  formatDateTime(value: string): string { return moment(value).format('DD/MM/YYYY HH:mm:ss'); }

  formatDay(value: string): string { return moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY'); }

  leadRoute(id: number) { return { name: 'Lead', params: { code: leadRouteCode(id) } }; }

  stoppingText(item: RuleExecution): string | null {
    if (item.outcome !== 'not_triggered') return null;
    if (item.ruleWhen === 'one') return 'nessuna condizione vera';
    const node = stoppingNode(item);
    return node ? nodeLabel(node, this.conditionDefinitions) : null;
  }

  setFilter(key: string, value: string | number | null | undefined) {
    const query: Record<string, string> = { ...(this.$route.query as Record<string, string>) };
    const v = value === null || value === undefined ? '' : String(value).trim();
    if (v) query[key] = v;
    else delete query[key];
    if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return;
    this.$router.replace({ query });
  }

  @Watch('$route.query', { deep: true })
  onQueryChanged() {
    this.leadInput = this.filters.lead;
    this.details = {};
    this.detailErrors = {};
    this.expanded = [];
    if (this.tableOptions.page !== 1) {
      // Il watcher delle opzioni ricarica dalla prima pagina
      this.tableOptions = { ...this.tableOptions, page: 1 };
      return;
    }
    this.load();
  }

  @Watch('tableOptions', { deep: true })
  onOptionsChanged() {
    this.load();
  }

  async load() {
    this.requestSeq += 1;
    const seq = this.requestSeq;
    this.loading = true;
    this.loadError = null;
    const f = this.filters;
    const page = this.tableOptions.page || 1;
    const [list, summary] = await Promise.all([
      API.getRuleExecutions({
        ruleId: f.ruleId || undefined,
        lead: f.lead || undefined,
        event: f.event || undefined,
        outcome: f.outcomes.length ? f.outcomes.join(',') : undefined,
        from: f.from,
        to: f.to,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
      API.getRuleRuns({
        lead: f.lead || undefined, event: f.event || undefined, from: f.from, to: f.to,
      }),
    ]);
    // Una risposta arrivata dopo un cambio di filtro non conta piu'
    if (seq !== this.requestSeq) return;
    this.loading = false;
    if (list instanceof Error || summary instanceof Error) {
      this.loadError = 'Non è stato possibile caricare le esecuzioni: controlla i filtri e riprova.';
      this.expanded = [];
      return;
    }
    this.items = list.items;
    this.total = list.total;
    this.summary = summary;
    this.expanded = [];
  }

  async onExpanded({ item, value }: { item: RuleExecution; value: boolean }) {
    if (!value) return;
    const cached = this.details[item.id];
    // Con consegne ancora in coda il dettaglio in cache e' vecchio: si ricarica a ogni apertura
    const hasQueued = !!cached && (cached.deliveries || []).some((d) => d.status === 'queued');
    if (cached && !hasQueued) return;
    this.$delete(this.detailErrors, item.id);
    const detail = await API.getRuleExecution(item.id);
    if (detail instanceof Error) {
      this.$set(this.detailErrors, item.id, true);
      return;
    }
    this.$set(this.details, item.id, detail);
  }

  async created() {
    this.leadInput = this.filters.lead;
    await Promise.all([
      this.$store.dispatch('fetchRules'),
      this.$store.dispatch('fetchRuleMetadata'),
    ]);
  }
}
